import React, { useState, useEffect } from 'react';
import SparklesIcon from './icons/SparklesIcon';
import CartIcon from './icons/CartIcon';
import UsersIcon from './icons/UsersIcon';
import CogIcon from './icons/CogIcon';
import CalendarPlusIcon from './icons/CalendarPlusIcon';
import type { Language } from '../types';
import { getTranslation } from '../utils/translations';

interface HowToModalProps {
    isOpen: boolean;
    onClose: () => void;
    language?: Language;
}

const HowToModal: React.FC<HowToModalProps> = ({ isOpen, onClose, language }) => {
    const [step, setStep] = useState(1);
    const [coords, setCoords] = useState({ top: 0, left: 0, width: 0, height: 0 });
    const [containerSize, setContainerSize] = useState({ width: 360, height: 600 });
    const t = (key: any) => getTranslation(language, key);

    useEffect(() => {
        if (isOpen) {
            setStep(1);
        }
    }, [isOpen]);

    const steps = [
        {
            id: 1,
            targetId: 'bottom-nav-tab-recipes',
            placement: 'top',
            title: t('tutorial_step1'),
            desc: t('tutorial_desc1'),
            tip: t('tutorial_tip1'),
            icon: <SparklesIcon className="w-8 h-8 text-sky-500" />
        },
        {
            id: 2,
            targetId: 'bottom-nav-tab-plan',
            placement: 'top',
            title: t('tutorial_step2'),
            desc: t('tutorial_desc2'),
            tip: t('tutorial_tip2'),
            icon: <CalendarPlusIcon className="w-8 h-8 text-amber-500" />
        },
        {
            id: 3,
            targetId: 'bottom-nav-tab-lists',
            placement: 'top',
            title: t('tutorial_step3'),
            desc: t('tutorial_desc3'),
            tip: t('tutorial_tip3'),
            icon: <CartIcon className="w-8 h-8 text-emerald-500" />
        },
        {
            id: 4,
            targetId: 'header-active-cart-button',
            placement: 'bottom',
            title: t('tutorial_step4'),
            desc: t('tutorial_desc4'),
            tip: t('tutorial_tip4'),
            icon: <UsersIcon className="w-8 h-8 text-sky-500" />
        },
        {
            id: 5,
            targetId: 'header-fasting-indicator',
            placement: 'bottom',
            title: t('tutorial_step5'),
            desc: t('tutorial_desc5'),
            tip: t('tutorial_tip5'),
            icon: <CogIcon className="w-8 h-8 text-purple-500" />
        },
        {
            id: 6,
            targetId: 'header-household-button',
            placement: 'bottom',
            title: t('tutorial_step6'),
            desc: t('tutorial_desc6'),
            tip: t('tutorial_tip6'),
            icon: <UsersIcon className="w-8 h-8 text-emerald-500" />
        }
    ];

    const currentStep = steps.find(s => s.id === step)!;

    useEffect(() => {
        if (!isOpen) return;

        const updateCoords = () => {
            let targetEl = document.getElementById(currentStep.targetId);
            if (!targetEl && currentStep.targetId === 'header-fasting-indicator') {
                // Fallback to menu button if fasting tracker UI is disabled
                targetEl = document.getElementById('header-menu-button');
            }

            const containerEl = document.getElementById('app-container') || document.body;
            const containerRect = containerEl.getBoundingClientRect();
            setContainerSize({
                width: containerRect.width,
                height: containerRect.height
            });

            if (targetEl) {
                const targetRect = targetEl.getBoundingClientRect();
                setCoords({
                    top: targetRect.top - containerRect.top,
                    left: targetRect.left - containerRect.left,
                    width: targetRect.width,
                    height: targetRect.height
                });
            } else {
                setCoords({ top: 0, left: 0, width: 0, height: 0 });
            }
        };

        // Give a short timeout for rendering/switching tabs to settle
        const timer = setTimeout(updateCoords, 100);
        window.addEventListener('resize', updateCoords);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', updateCoords);
        };
    }, [step, isOpen, currentStep.targetId]);

    if (!isOpen) return null;

    const isBottomTab = currentStep.placement === 'top';
    const tooltipStyle: React.CSSProperties = isBottomTab
        ? { bottom: '130px' }
        : { top: '110px' };

    // Draw quadratic curve arrow from the middle of the tooltip to the highlighted target element
    let arrowPath = '';
    if (coords.width > 0) {
        const x1 = containerSize.width / 2;
        const y1 = isBottomTab ? containerSize.height - 130 : 110;
        const x2 = coords.left + coords.width / 2;
        const y2 = isBottomTab ? coords.top - 8 : coords.top + coords.height + 8;
        const controlX = (x1 + x2) / 2;
        const controlY = isBottomTab ? (y1 + y2) / 2 + 10 : (y1 + y2) / 2 - 10;
        arrowPath = `M ${x1} ${y1} Q ${controlX} ${controlY} ${x2} ${y2}`;
    }

    return (
        <div className="absolute inset-0 z-[140] overflow-hidden" onClick={onClose}>
            {/* SVG Mask and Curved Arrow */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none z-10">
                <defs>
                    <mask id="spotlight-mask">
                        <rect x="0" y="0" width="100%" height="100%" fill="white" />
                        {coords.width > 0 && (
                            <rect
                                x={coords.left - 6}
                                y={coords.top - 6}
                                width={coords.width + 12}
                                height={coords.height + 12}
                                rx="12"
                                fill="black"
                            />
                        )}
                    </mask>
                    <marker
                        id="arrowhead"
                        markerWidth="10"
                        markerHeight="7"
                        refX="10"
                        refY="3.5"
                        orient="auto"
                    >
                        <polygon points="0 0, 10 3.5, 0 7" fill="#f97316" />
                    </marker>
                </defs>
                
                {/* Spotlight backdrop with blur */}
                <rect
                    x="0"
                    y="0"
                    width="100%"
                    height="100%"
                    fill="rgba(15, 23, 42, 0.65)"
                    mask="url(#spotlight-mask)"
                    className="backdrop-blur-[3px] pointer-events-auto"
                    onClick={onClose}
                />
                
                {/* Premium Animated Curved Arrow */}
                {coords.width > 0 && (
                    <path
                        d={arrowPath}
                        fill="none"
                        stroke="#f97316"
                        strokeWidth="3.5"
                        strokeLinecap="round"
                        strokeDasharray="4 4"
                        markerEnd="url(#arrowhead)"
                        className="animate-pulse"
                    />
                )}
            </svg>

            {/* Premium Tour Tooltip Card */}
            <div 
                style={tooltipStyle}
                className="absolute left-4 right-4 w-[calc(100%-2rem)] max-w-sm bg-white dark:bg-slate-900 rounded-3xl shadow-2xl z-20 p-5 border border-slate-100 dark:border-slate-800/80 animate-fade-in flex flex-col gap-4"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center gap-3">
                    <div className="bg-sky-50 dark:bg-sky-900/20 p-2.5 rounded-2xl shadow-sm">
                        {currentStep.icon}
                    </div>
                    <div className="min-w-0 flex-1">
                        <span className="text-[10px] font-black text-brand-orange uppercase tracking-wider block mb-0.5">
                            {t('tutorial_step')} {step} / {steps.length}
                        </span>
                        <h3 className="text-base font-black text-slate-800 dark:text-slate-100 truncate">
                            {currentStep.title}
                        </h3>
                    </div>
                </div>

                <p className="text-slate-600 dark:text-slate-400 leading-relaxed text-xs">
                    {currentStep.desc}
                </p>

                {currentStep.tip && (
                    <div className="bg-slate-50 dark:bg-slate-800/50 px-3 py-2.5 rounded-xl border border-slate-100 dark:border-slate-800 flex items-center gap-2.5 text-start">
                        <span className="text-xs">💡</span>
                        <p className="text-[10px] font-black text-slate-700 dark:text-slate-300 uppercase tracking-wider">{currentStep.tip}</p>
                    </div>
                )}

                <div className="flex justify-between items-center mt-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                    <button 
                        onClick={() => setStep(prev => Math.max(1, prev - 1))}
                        disabled={step === 1}
                        className="text-xs text-slate-400 dark:text-slate-500 font-bold hover:text-slate-600 dark:hover:text-slate-400 disabled:opacity-0 transition-opacity"
                    >
                        {t('previous')}
                    </button>
                    
                    <div className="flex gap-2">
                        <button
                            onClick={onClose}
                            className="px-3 py-2 text-xs font-bold text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                        >
                            {t('skip')}
                        </button>
                        {step < steps.length ? (
                            <button 
                                onClick={() => setStep(prev => prev + 1)}
                                className="px-5 py-2 bg-brand-orange text-white text-xs font-black rounded-lg hover:bg-orange-600 shadow-md shadow-orange-100 dark:shadow-none transition-all animate-pulse"
                            >
                                {t('nextStep')}
                            </button>
                        ) : (
                            <button 
                                onClick={onClose}
                                className="px-5 py-2 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-xs font-black rounded-lg hover:bg-slate-800 dark:hover:bg-slate-200 shadow-md transition-all"
                            >
                                {t('gotIt')}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HowToModal;